import { logger } from "@/lib/logger";
/**
 * useLanguagePersistence
 *
 * Resolution order on mount/auth change:
 *   1. URL `?lang=xx` (highest — explicit deep link)
 *   2. profiles.preferred_language (logged-in users — cross-device)
 *   3. localStorage 'ptc-language'
 *   4. current i18n.language (no-op)
 *
 * Also keeps the URL `?lang` param in sync with the active language so
 * shared links carry the variant, and writes to profile when the user
 * changes language while authenticated.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const SUPPORTED = new Set([
  "en", "sw", "fr", "so", "pt", "ar", "af", "zu", "pcm", "ha", "ig",
  "ny", "mg", "xh", "rw", "id", "th", "vi", "ko", "ms", "st",
]);

function readUrlLang(): string | null {
  if (typeof window === "undefined") return null;
  const lang = new URLSearchParams(window.location.search).get("lang");
  return lang && SUPPORTED.has(lang) ? lang : null;
}

function writeUrlLang(lang: string) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.get("lang") === lang) return;
  url.searchParams.set("lang", lang);
  window.history.replaceState({}, "", url.toString());
}

export function useLanguagePersistence() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const initialized = useRef(false);

  // Initial resolution: URL → profile → localStorage
  useEffect(() => {
    if (initialized.current) return;

    const urlLang = readUrlLang();
    if (urlLang) {
      initialized.current = true;
      if (urlLang !== i18n.language) i18n.changeLanguage(urlLang);
      localStorage.setItem("ptc-language", urlLang);
      return;
    }

    const apply = (lang: string | null) => {
      if (lang && SUPPORTED.has(lang) && lang !== i18n.language) {
        i18n.changeLanguage(lang);
        localStorage.setItem("ptc-language", lang);
      }
      writeUrlLang(lang || i18n.language);
      initialized.current = true;
    };

    if (user) {
      supabase
        .from("profiles")
        .select("preferred_language")
        .eq("user_id", user.id)
        .maybeSingle()
        .then(({ data }) => {
          const profileLang = (data as { preferred_language?: string } | null)?.preferred_language ?? null;
          apply(profileLang ?? localStorage.getItem("ptc-language"));
        });
    } else {
      apply(localStorage.getItem("ptc-language"));
    }
  }, [user, i18n]);

  // Keep URL in sync + persist to profile when language changes
  useEffect(() => {
    const onChange = (lang: string) => {
      writeUrlLang(lang);
      localStorage.setItem("ptc-language", lang);
      if (user) {
        supabase
          .from("profiles")
          .update({ preferred_language: lang })
          .eq("user_id", user.id)
          .then(({ error }) => {
            if (error) logger.warn("[lang] persist failed:", error.message);
          });
      }
    };
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, [user, i18n]);
}
